import { TeeBox } from '@/types';
import { GolfCourseApiTeeSet, TeeMapping } from '@/types/golfCourseApi';

const TEE_COLOR_MAP: Record<string, TeeBox> = {
  blue: 'blue',
  white: 'white',
  gold: 'gold',
  red: 'red',
  black: 'black',
};

const ALL_DB_COLORS: TeeBox[] = ['red', 'gold', 'white', 'blue', 'black'];

/**
 * Build TeeMapping[] from the API tees dict ({ male: [...], female: [...] }).
 * - Known colors (Blue, White, Gold, Red, Black) auto-map.
 * - When male + female share a tee name, combine into one entry preferring male yardages.
 * - Female-only tees get labeled "Women's".
 */
export function autoMapTees(tees: Record<string, GolfCourseApiTeeSet[]>): TeeMapping[] {
  // Flatten into entries tagged with gender
  const tagged: { gender: string; tee: GolfCourseApiTeeSet }[] = [];
  for (const [gender, teeList] of Object.entries(tees)) {
    if (!Array.isArray(teeList)) continue;
    for (const tee of teeList) {
      tagged.push({ gender, tee });
    }
  }

  // Group by tee name (case-insensitive)
  const grouped = new Map<string, { gender: string; tee: GolfCourseApiTeeSet }[]>();
  for (const entry of tagged) {
    const key = entry.tee.tee_name.toLowerCase();
    const existing = grouped.get(key) || [];
    existing.push(entry);
    grouped.set(key, existing);
  }

  const mappings: TeeMapping[] = [];

  for (const [key, variants] of grouped) {
    const male = variants.find(v => v.gender === 'male');
    const female = variants.find(v => v.gender === 'female');

    // Prefer male variant; if only female exists, use it
    const primary = male || female!;
    const gender = male && female
      ? 'Both'
      : male
        ? 'Men\'s'
        : 'Women\'s';

    const dbColor = TEE_COLOR_MAP[key] ?? null;

    mappings.push({
      apiTeeName: primary.tee.tee_name,
      gender,
      totalYards: primary.tee.total_yards,
      parTotal: primary.tee.par_total,
      numberOfHoles: primary.tee.number_of_holes || primary.tee.holes.length,
      dbColor,
      holes: primary.tee.holes,
    });
  }

  return mappings;
}

/**
 * Returns DB colors still available for the mapping at `currentIndex`.
 * A color is available if no other mapping (at a different index) is already using it.
 */
export function getAvailableColors(
  mappings: TeeMapping[],
  currentIndex: number
): TeeBox[] {
  const usedColors = new Set<TeeBox>();
  mappings.forEach((m, i) => {
    if (i !== currentIndex && m.dbColor) {
      usedColors.add(m.dbColor);
    }
  });
  return ALL_DB_COLORS.filter(c => !usedColors.has(c));
}

/** Returns true if at least one tee is mapped to a DB color */
export function hasAtLeastOneMapping(mappings: TeeMapping[]): boolean {
  return mappings.some(m => m.dbColor !== null);
}
